/*
 * Easing picker, Bezier editor, curve-part editor, value plot and templates dialog (W2-05). Design tokens only
 * (src/ui/styles/tokens.css); reduced motion is handled globally (global.css zeroes the durations).
 */

/* ---------------------------------------------------------------- shared */

.note {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  line-height: var(--leading-normal);
}

.muted {
  color: var(--color-text-subtle);
  font-size: var(--text-sm);
}

.error {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--text-sm);
}

.warnText {
  color: var(--color-warning);
  font-size: var(--text-sm);
}

.warning {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-warning);
  border-radius: var(--radius-md);
  background: var(--color-warning-muted);
  color: var(--color-text);
  font-size: var(--text-sm);
}

.warning p {
  margin: 0;
}

.hint {
  margin: 0;
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  background: var(--color-info-muted);
  color: var(--color-text);
  font-size: var(--text-sm);
}

.input {
  height: var(--control-h-sm);
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  color: var(--color-text);
  font-family: var(--font-mono);
  font-size: var(--text-sm);
  min-width: 0;
}

.input:focus-visible,
.searchInput:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.inlineRow {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
}

.fieldLabel {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  min-width: var(--label-width);
}

.notes {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

/* ---------------------------------------------------------------- picker */

/*
 * The picker keeps its header and footer (Cancel / Use this curve) on screen: the body scrolls when the window is
 * too short (UX-044). `--picker-max-h` is set from the surface's position, also after a window resize.
 */
.picker {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: min(760px, calc(100vw - 32px));
  max-height: min(86vh, 680px, var(--picker-max-h, 100vh));
  padding: var(--space-4);
  overflow: hidden;
  color: var(--color-text);
  font-size: var(--text-md);
}

.pickerHeader {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

.title {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
}

.search {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: 0 var(--space-2);
  border: var(--border-width) solid var(--color-border-input);
  border-radius: var(--radius-sm);
  background: var(--color-surface-sunken);
  color: var(--color-text-muted);
}

.searchInput {
  width: 220px;
  height: var(--control-h-sm);
  border: none;
  background: transparent;
  color: var(--color-text);
  font-size: var(--text-sm);
}

.pickerBody {
  display: grid;
  flex: 1 1 auto;
  grid-template-columns: minmax(0, 1fr) 240px;
  gap: var(--space-4);
  min-height: 0;
  overflow: auto;
}

@media (max-width: 720px) {
  .pickerBody {
    grid-template-columns: minmax(0, 1fr);
  }
}

.pickerMain {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.pickerSide {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  min-width: 0;
}

.pickerFooter {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
  padding-top: var(--space-2);
  border-top: var(--border-width) solid var(--color-divider);
}

.count {
  flex: 1 1 200px;
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.keysHint {
  flex: 1 1 100%;
  order: 3;
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

.footerButtons {
  display: flex;
  gap: var(--space-2);
  margin-left: auto;
}

/* grid */

.grid {
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
}

.topRow {
  display: flex;
  gap: var(--space-2);
  margin-bottom: var(--space-2);
}

.topRow .gridCell {
  flex: 1 1 0;
}

.topRow .cell {
  justify-content: flex-start;
  width: 100%;
}

.gridHead,
.gridRow {
  display: grid;
  grid-template-columns: 96px repeat(3, minmax(0, 1fr));
  align-items: center;
  gap: var(--space-1);
}

.colHead {
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  text-align: center;
}

.rowHead {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.gridCell {
  display: flex;
  min-width: 0;
}

.cell {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: calc(36px * var(--ui-scale));
  padding: var(--space-0) var(--space-2);
  border: var(--border-width) solid transparent;
  border-radius: var(--radius-sm);
  background: var(--color-surface-2);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
  transition: background var(--duration-fast) var(--ease-standard);
}

.cell:hover {
  background: var(--color-surface-hover);
}

.cell[data-focused] {
  border-color: var(--color-accent-border);
  background: var(--color-accent-muted);
}

.cell:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.cell[aria-current='true'] {
  box-shadow: inset 0 0 0 var(--border-width) var(--color-border-strong);
}

.cell[aria-disabled='true'] {
  background: var(--color-surface-1);
  color: var(--color-text-disabled);
  cursor: not-allowed;
  opacity: 0.6;
}

.cellText {
  white-space: nowrap;
}

.cellId {
  position: absolute;
  top: var(--space-0);
  right: var(--space-1);
  color: var(--color-text-subtle);
  font-family: var(--font-mono);
  font-size: var(--text-2xs);
  visibility: hidden;
}

.cellId[data-always],
.cell:hover .cellId,
.cell:focus-visible .cellId,
.cell[data-focused] .cellId {
  visibility: visible;
}

.cellBadges {
  position: absolute;
  bottom: var(--space-0);
  right: var(--space-1);
  display: flex;
  gap: var(--space-0);
  color: var(--color-text-muted);
}

/* search results */

.results {
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
  margin: 0;
  padding: 0;
  list-style: none;
}

.result {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  cursor: pointer;
}

.result[data-active] {
  background: var(--color-accent-muted);
}

.result[aria-disabled='true'] {
  color: var(--color-text-disabled);
}

.resultReason {
  margin-left: auto;
  color: var(--color-text-subtle);
  font-size: var(--text-xs);
}

/* preview */

.previewBox {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-1);
}

.previewTitle,
.chipsTitle,
.presetsTitle,
.sliceTitle {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--color-text-muted);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.candidate {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
  font-size: var(--text-sm);
}

.plot {
  display: block;
  max-width: 100%;
  height: auto;
}

.plotGuide {
  stroke: var(--color-curve-guide);
  stroke-width: 1;
}

.plotOvershoot {
  fill: var(--color-curve-overshoot);
}

.plotCurve {
  fill: none;
  stroke: var(--color-curve);
  stroke-width: 2;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.plotCompare {
  fill: none;
  stroke: var(--color-text-subtle);
  stroke-width: 1.5;
  stroke-dasharray: 4 3;
}

.plotDot {
  fill: var(--color-playhead);
}

.plotValue {
  fill: var(--color-text-muted);
  font-size: var(--text-2xs);
  font-family: var(--font-mono);
}

/* chips */

.chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
}

.chipsTitle {
  flex: 1 1 100%;
}

.chip {
  display: inline-flex;
  padding: var(--space-0);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface-2);
  cursor: pointer;
}

.chip:hover {
  background: var(--color-surface-hover);
}

.chip:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.chip[aria-disabled='true'] {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ---------------------------------------------------------------- slice */

.slice {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.slice[data-disabled] {
  opacity: 0.7;
}

.sliceHeader {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.sliceRange {
  color: var(--color-text-muted);
  font-family: var(--font-mono);
  font-size: var(--text-xs);
}

.sliceRow {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.sliceBar {
  position: relative;
  flex: 1 1 auto;
  height: 20px;
  touch-action: none;
}

.sliceTrack {
  position: absolute;
  top: 8px;
  right: 0;
  left: 0;
  height: 4px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-3);
}

.sliceRangeFill {
  position: absolute;
  top: 8px;
  height: 4px;
  border-radius: var(--radius-pill);
  background: var(--color-accent);
}

.sliceThumb {
  position: absolute;
  top: 2px;
  width: 14px;
  height: 14px;
  margin-left: -7px;
  border: 2px solid var(--color-accent);
  border-radius: var(--radius-pill);
  background: var(--color-surface-overlay);
  cursor: grab;
  touch-action: none;
}

.sliceThumb:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.sliceThumb[aria-disabled='true'] {
  border-color: var(--color-text-disabled);
  cursor: not-allowed;
}

.sliceFields {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-2);
}

/* ---------------------------------------------------------------- bezier */

.bezier {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.bezierMain {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-4);
}

.bezierSide {
  display: flex;
  flex: 1 1 220px;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.bezierFields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-2);
}

.bezierCanvas {
  flex: 0 0 auto;
  border-radius: var(--radius-md);
  background: var(--color-surface-sunken);
  touch-action: none;
}

.bezierFrame {
  fill: var(--color-surface-1);
  stroke: var(--color-curve-guide);
  stroke-width: 1;
}

.bezierOvershoot {
  fill: var(--color-curve-overshoot);
}

.bezierCurve {
  fill: none;
  stroke: var(--color-curve);
  stroke-width: 2.5;
  stroke-linejoin: round;
}

.bezierArm {
  stroke: var(--color-text-muted);
  stroke-width: 1.5;
}

.bezierHandle {
  fill: var(--color-surface-overlay);
  stroke: var(--color-accent);
  stroke-width: 2.5;
  cursor: grab;
}

.bezierHandle:focus-visible {
  outline: none;
  stroke: var(--color-focus-ring);
  stroke-width: 4;
}

.pasteRow {
  display: flex;
  align-items: flex-end;
  gap: var(--space-2);
}

.pasteLabel {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: var(--space-1);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  min-width: 0;
}

/* The title on its own line, the preset buttons wrapping under it (UX-048: no chip starting under the title). */
.presets {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
}

.presets > .presetsTitle {
  flex: 1 1 100%;
}

.bezierDialog {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 240px;
  align-items: start;
  gap: var(--space-4);
}

@media (max-width: 860px) {
  .bezierDialog {
    grid-template-columns: minmax(0, 1fr);
  }
}

/* The key handlers around a dialog footer's buttons: no box of its own, the footer lays the buttons out. */
.keysScope {
  display: contents;
}

/* ---------------------------------------------------------------- templates */

.templates {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.templateChooser {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
  gap: var(--space-1);
}

.templateButton {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  height: var(--control-h-md);
  padding: 0 var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-sm);
  background: var(--color-surface-2);
  color: var(--color-text);
  font: inherit;
  font-size: var(--text-sm);
  cursor: pointer;
}

.templateButton:hover {
  background: var(--color-surface-hover);
}

.templateButton[aria-checked='true'] {
  border-color: var(--color-accent-border);
  background: var(--color-accent-muted);
}

.templateButton:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

.templateFields {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-surface-1);
}

.curveRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.templateFooterNote {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}
